import { Checkbox, TableCell, TableHead, TableRow, TableSortLabel, Typography } from '@mui/material';
import { RaRecord, useListContext, useResourceContext, useTranslateLabel } from 'ra-core';
import React, { ReactElement, ReactNode, useCallback, useMemo } from 'react';
import { DatagridHeaderProps } from 'react-admin';

type SortItem = {
  field: string;
  order: 'ASC' | 'DESC';
};

interface MultiSortDatagridHeaderProps extends Omit<DatagridHeaderProps, 'setSort'> {
  multiSort?: SortItem[];
  onSortClick?: (field: string) => void;
  setMultiSort?: (sort: SortItem[]) => void;
}

function DatagridHeader(props: MultiSortDatagridHeaderProps): ReactElement {
  const {
    children,
    hasExpand,
    hasBulkActions,
    isRowSelectable,
    onSelect,
    selectedIds = [],
    data = [],
    multiSort = [],
    setMultiSort,
    onSortClick,
    resource: resourceProp
  } = props;
  const { setSort } = useListContext();
  const translateLabel = useTranslateLabel();
  const resourceContext = useResourceContext();
  const resource = resourceProp ?? resourceContext;

  const updateSort = useCallback(
    (field: string, isMultiSort: boolean = false) => {
      if (onSortClick) onSortClick(field);
      if (!setMultiSort) {
        const currentSort = multiSort.find((s) => s.field === field);
        const newOrder = currentSort?.order === 'ASC' ? 'DESC' : 'ASC';
        setSort({ field, order: newOrder });
        return;
      }

      const existingIndex = multiSort.findIndex((s) => s.field === field);

      if (!isMultiSort) {
        if (existingIndex >= 0) {
          const currentOrder = multiSort[existingIndex].order;
          if (currentOrder === 'ASC') {
            setMultiSort([{ field, order: 'DESC' }]);
          } else {
            setMultiSort([]);
          }
        } else {
          setMultiSort([{ field, order: 'ASC' }]);
        }
        return;
      }

      const newMultiSort = [...multiSort];

      if (existingIndex >= 0) {
        const currentOrder = newMultiSort[existingIndex].order;
        if (currentOrder === 'ASC') {
          newMultiSort[existingIndex] = { field, order: 'DESC' };
        } else {
          newMultiSort.splice(existingIndex, 1);
        }
      } else {
        newMultiSort.push({ field, order: 'ASC' });
      }

      setMultiSort(newMultiSort);
    },
    [multiSort, setMultiSort, setSort, onSortClick]
  );

  const handleSelectAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!onSelect) {
        return;
      }
      if (event.target.checked) {
        const allIds = data
          .filter((record: RaRecord) => (isRowSelectable ? isRowSelectable(record) : true))
          .map((record: RaRecord) => record.id);
        onSelect(allIds);
      } else {
        onSelect([]);
      }
    },
    [data, isRowSelectable, onSelect]
  );

  const getSortForField = useCallback(
    (field: string): { order: 'ASC' | 'DESC'; index: number } | null => {
      const index = multiSort.findIndex((s) => s.field === field);
      if (index >= 0) {
        return { order: multiSort[index].order, index };
      }
      return null;
    },
    [multiSort]
  );

  const childrenArray = useMemo(() => React.Children.toArray(children), [children]);

  const selectableCount = useMemo(
    () => data.filter((record: RaRecord) => (isRowSelectable ? isRowSelectable(record) : true)).length,
    [data, isRowSelectable]
  );

  const handleSortClick = useCallback(
    (source: string) => (event: React.MouseEvent<unknown>) => {
      updateSort(source, event.ctrlKey || event.metaKey);
    },
    [updateSort]
  );

  return (
    <TableHead>
      <TableRow>
        {!!hasExpand && <TableCell />}
        {!!hasBulkActions && (
          <TableCell padding="checkbox">
            <Checkbox
              color="primary"
              checked={selectedIds.length > 0 && selectedIds.length === selectableCount}
              onChange={handleSelectAll}
            />
          </TableCell>
        )}
        {childrenArray.length > 0 ? (
          childrenArray.map((child: ReactNode, index) => {
            if (!child || typeof child !== 'object' || !('props' in child)) {
              return <TableCell key={index} />;
            }

            const source =
              child.props.sortBy ??
              child.props.source ??
              (typeof child.props.label === 'string' ? child.props.label : undefined);
            const sortable = child.props.sortable !== false && !!source;
            const sortInfo = sortable ? getSortForField(source) : null;

            const translated = translateLabel({
              ...child.props,
              source: child.props.source ?? source,
              resource
            });

            const displayLabel: ReactNode = translated ?? child.props.label ?? null;

            return (
              <TableCell
                key={child.props.source ?? index}
                align={child.props.textAlign || 'left'}
                sx={{ cursor: sortable ? 'pointer' : 'default' }}
              >
                {sortable ? (
                  <TableSortLabel
                    active={!!sortInfo}
                    direction={(sortInfo?.order.toLowerCase() as 'asc' | 'desc') || 'asc'}
                    onClick={handleSortClick(source)}
                  >
                    <Typography>{displayLabel}</Typography>
                  </TableSortLabel>
                ) : (
                  displayLabel
                )}
              </TableCell>
            );
          })
        ) : (
          <TableCell />
        )}
      </TableRow>
    </TableHead>
  );
}

export { DatagridHeader };

export type { MultiSortDatagridHeaderProps, SortItem };
